import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';

// Generic hook for detecting scroll:
const useScrollAware = () => {
  const [scrollTop, setScrollTop] = useState(0);
  const animationFrame = useRef();

  const onScroll = useCallback(() => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    animationFrame.current = requestAnimationFrame(() => {
      setScrollTop(
        document.body.scrollTop || document.documentElement.scrollTop
      );
    });
  }, []);

  useEffect(() => {
    const throttledOnScroll = throttle(onScroll, 100);
    window.addEventListener('scroll', throttledOnScroll);
    return () => {
      throttledOnScroll.cancel();
      window.removeEventListener('scroll', throttledOnScroll);
    };
  }, [onScroll]);

  return scrollTop;
};

const findStartNode = (
  scrollTop,
  nodePositions,
  itemCount,
  actualPositions
) => {
  let startRange = 0;
  let endRange = itemCount - 1;
  while (endRange !== startRange) {
    // console.log(startRange, endRange);
    const middle = Math.floor((endRange - startRange) / 2 + startRange);

    const start = actualPositions[middle] ?? nodePositions[middle];
    const end = actualPositions[middle + 1] ?? nodePositions[middle + 1];
    if (start <= scrollTop && end > scrollTop) {
      // console.log("middle", middle);
      return middle;
    }

    if (middle === startRange) {
      // edge case - start and end range are consecutive
      // console.log("endRange", endRange);
      return endRange;
    }
    if (start <= scrollTop) {
      startRange = middle;
    } else {
      endRange = middle;
    }
  }
  return itemCount;
};

const findEndNode = (
  nodePositions,
  startNode,
  itemCount,
  height,
  actualPositions
) => {
  let endNode;
  for (endNode = startNode; endNode < itemCount; endNode += 1) {
    const satrt = actualPositions[startNode] || nodePositions[startNode];
    const end = actualPositions[endNode] || nodePositions[endNode];
    // console.log(nodePositions[endNode], nodePositions[startNode]);
    if (end > satrt + height) {
      // console.log(endNode);
      return endNode;
    }
  }
  return endNode;
};

const useVirtualScroll = ({
  itemCount,
  isAllItemsRendered,
  columnsWidth,
  renderAhead = 10,
  minHeight = 38,
  scrollHeight = 9,
  keepRenderedRows = false,
}) => {
  const { height } = window.screen;
  const maxRenderedCountRef = useRef(0);
  const [actualPositions, setActualPositions] = useState([0]);

  useEffect(() => {
    maxRenderedCountRef.current = 0;
  }, [itemCount]);
  const childPositions = useMemo(() => {
    const results = [0];
    for (let i = 1; i < itemCount; i += 1) {
      results.push(results[i - 1] + minHeight);
    }
    return results;
  }, [minHeight, itemCount]);

  const scrollTop = useScrollAware();
  let totalHeight = childPositions[itemCount - 1] + minHeight;

  const [heightTotal, setHeightTotal] = useState(totalHeight);

  const firstVisibleNode = useMemo(() => {
    if (itemCount === 0) return 0;
    return findStartNode(scrollTop, childPositions, itemCount, actualPositions);
  }, [scrollTop, childPositions, itemCount, actualPositions]);

  let startNode = Math.max(0, firstVisibleNode - renderAhead);

  const lastVisibleNode = useMemo(
    () =>
      findEndNode(
        childPositions,
        firstVisibleNode,
        itemCount,
        height,
        actualPositions
      ),
    [childPositions, firstVisibleNode, itemCount, height, actualPositions]
  );
  const endNode = Math.min(itemCount - 1, lastVisibleNode + renderAhead);
  let visibleNodeCount = endNode - startNode + 1;
  const virtualStartNode = startNode;
  const virtualEndNode = endNode;

  const refScroll = useRef();

  useEffect(() => {
    const viewportEL = document.querySelector(
      '.scrollable:not(.d-none) .viewport'
    );
    if (!viewportEL) return;

    const tbodyEl = viewportEL.querySelector('tbody');
    const positions = [...actualPositions];
    const measureStartNode = keepRenderedRows ? 0 : virtualStartNode;
    const measureEndNode = keepRenderedRows
      ? Math.min(itemCount - 1, maxRenderedCountRef.current - 1)
      : virtualEndNode;

    for (let i = 0; i < itemCount; i += 1) {
      if (measureStartNode + i >= measureEndNode) {
        break;
      }
      positions[measureStartNode + i + 1] =
        (positions[measureStartNode + i] ??
          childPositions[measureStartNode + i]) +
        (tbodyEl.childNodes[i]?.offsetHeight ?? minHeight);
    }
    setActualPositions(positions);

    if (lastVisibleNode === virtualEndNode + 1) {
      const bottomTbody = tbodyEl.getBoundingClientRect().bottom;
      const bottomViewport = viewportEL.getBoundingClientRect().bottom;
      setHeightTotal(
        heightTotal + scrollHeight + (bottomTbody - bottomViewport)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualStartNode, virtualEndNode, columnsWidth, keepRenderedRows]);

  const heightTotalAcual = actualPositions[itemCount - 1];
  useEffect(() => {
    setHeightTotal(
      heightTotalAcual > heightTotal ? heightTotalAcual : heightTotal
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightTotalAcual]);

  let offsetY = actualPositions[startNode] ?? childPositions[startNode];
  totalHeight = heightTotal;

  if (keepRenderedRows && !isAllItemsRendered) {
    maxRenderedCountRef.current = Math.max(
      maxRenderedCountRef.current,
      endNode + 1
    );
    startNode = 0;
    visibleNodeCount = Math.min(itemCount, maxRenderedCountRef.current);
    offsetY = 0;
  }

  if (isAllItemsRendered) {
    totalHeight = 'auto';
    offsetY = 0;
    startNode = 0;
    visibleNodeCount = itemCount;
  }

  return { startNode, visibleNodeCount, offsetY, totalHeight, refScroll };
};

export default useVirtualScroll;
